import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Webcam from 'react-webcam';
import { Button } from '@components/atoms/button';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { Icon } from '@components/atoms/icon';
import { Spinner } from '@components/atoms/spinner';
import { CloseIcon } from '@components/icons/close-icon';
import {
    DocsVerificationBiometricTypeEnum,
    DocsVerificationInternalStepEnum,
    DocsVerificationLivenessEnum,
    DocsVerificationLivenessTagErrorEnum,
    OrganizationRoleEnum,
} from '@models/enums';
import type { FaceDetectResult, FileWithPreview, ThemeProps } from '@models/types';
import { useCameraConstraints } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/hooks/use-camera-constraints';
import { useDemoData } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/demo-data-provider';
import { useWidgetContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import { Mask } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/mask';
import { FileErrorCodeEnum } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/types/file-error-code-enum';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { RootState } from '@store/root-reducer';
import * as faceDetection from '@tensorflow-models/face-detection';
import type { Face, FaceLandmarksDetector } from '@tensorflow-models/face-landmarks-detection';
import { getEnvVar } from '@utils/get-env-var';
import { base64ToFile, isAndroid, sleep } from '@utils/util';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import {
    FACE_DETECTOR_CONFIG,
    FACE_DETECTOR_LOADING_TIMEOUT,
} from '../../../../../docs-verification-steps/constants';

import { detectFace, MIN_FACE_SIZE_PERCENT } from './utils/detect-face';
import { Hints } from './hints';
import { ProcessLivenessContainer, WebCamContainer } from './styles';
import type { FacePositionsState, LivenessProps } from './types';
import { VerificationStatusEnum } from './types';

import '@tensorflow/tfjs-backend-webgl';

const defaultFacePositions = {
    [DocsVerificationLivenessEnum.CENTER]: false,
    [DocsVerificationLivenessEnum.UP]: false,
    [DocsVerificationLivenessEnum.RIGHT]: false,
    [DocsVerificationLivenessEnum.DOWN]: false,
    [DocsVerificationLivenessEnum.LEFT]: false,
};

const MIN_DETECT_COUNT = 3;
const ERROR_PAUSE_DELAY = 5000;
const PREPARE_SECONDS = 3;
const SUCCESS_DETECT_COUNT = 4;

export const LivenessProcess: React.FC<
    LivenessProps & {
        onSubmit: (isFailedVerification?: boolean) => Promise<boolean>;
        onClose: () => void;
    }
> = ({
    titleCameraNotAllowed,
    onFileChange,
    // checkStatus,
    onSubmit,
    onClose,
    className,
}) => {
    const filesToUpload = useRef<FileWithPreview[]>([]);
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const { scrollTop } = useWidgetContext();
    const { uploadFileRef } = useDemoData();
    const { cameraConstraints } = useCameraConstraints();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const webContainerRef = useRef<HTMLDivElement | null>(null);
    const faceDetectInterval = useRef<ReturnType<typeof setInterval> | null>(null);
    const userRole = useSelector(
        (state: RootState) => state?.dashboard?.session?.organizationInfo?.organization_role,
    ) as OrganizationRoleEnum;
    const { t } = useTranslation();
    const [isCameraNotAllowed, setIsCameraNotAllowed] = useState(false);
    const [currentStep, setCurrentStep] = useState<DocsVerificationInternalStepEnum>(
        DocsVerificationInternalStepEnum.CAMERA_ACCESS,
    );
    const webcamRef = useRef<Webcam>(null);
    const isInitialized = useRef(false);
    const isDone = useRef(false);
    const faceDetectorFromStore = useSelector(
        (state: RootState) => state.docsVerification.faceDetector,
    );
    const faceDetector = useRef<FaceLandmarksDetector | null>(
        faceDetectorFromStore as FaceLandmarksDetector,
    );
    const facePositions = useRef<FacePositionsState>(defaultFacePositions);
    const failedChecksCount = useRef(0);
    const faceCount = useRef(1);
    const [status, setStatus] = useState<VerificationStatusEnum | null>(null);
    const isPaused = useRef(false);
    const isDetectInProgress = useRef(false);
    const detectCount = useRef(0);
    const successDetectCount = useRef(0);
    const [faceDetectResult, setFaceDetectResult] = useState<FaceDetectResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [prepareSeconds, setPrepareSeconds] = useState<number>(PREPARE_SECONDS);
    const [isMaskReady, setIsMaskReady] = useState(false);

    const maskReady = useCallback(() => {
        setIsMaskReady(true);
    }, []);

    const getWebcamFile = useCallback(async () => {
        const imageSrc = webcamRef.current?.getScreenshot(
            matchesMobileSm && !isAndroid() && !theme.isLandscape
                ? { width: 800, height: 600 }
                : undefined,
        );

        let fileWithPreview = (await base64ToFile(
            imageSrc as string,
            `photo_${uuidv4()}.jpeg`,
        )) as FileWithPreview;

        fileWithPreview = Object.assign(fileWithPreview, {
            preview: imageSrc,
        });

        return fileWithPreview;
    }, [matchesMobileSm, theme.isLandscape]);

    const capture = useCallback(
        async ({
            tag,
            detectResult,
            isIgnoreCheck,
            file,
        }: {
            documentType: DocsVerificationBiometricTypeEnum;
            tag?: DocsVerificationLivenessTagErrorEnum;
            detectResult?: FaceDetectResult;
            isIgnoreCheck: boolean;
            file: FileWithPreview;
        }) => {
            if (onFileChange) {
                if (isIgnoreCheck) {
                    await onFileChange(file, tag, detectResult);
                } else {
                    setStatus(VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS);
                    const [verificationFileState] = await Promise.all([
                        onFileChange(filesToUpload.current, tag, detectResult),
                        sleep(5000),
                    ]);

                    filesToUpload.current = [];

                    if (verificationFileState.status.success) {
                        isPaused.current = true;
                        setStatus(VerificationStatusEnum.SUCCESS);
                        await sleep(4000);
                        const isSuccess = await onSubmit(
                            verificationFileState.status.isFailedVerification,
                        );

                        if (isSuccess) {
                            isDone.current = true;
                        } else {
                            isPaused.current = false;
                            setStatus(VerificationStatusEnum.ALIGN_FACE);
                        }
                    } else {
                        if (
                            verificationFileState.status.message?.includes(
                                String(FileErrorCodeEnum.POOR_LIGHTING_CONDITIONS),
                            )
                        ) {
                            isPaused.current = true;
                            setTimeout(() => {
                                isPaused.current = false;
                            }, ERROR_PAUSE_DELAY);
                            detectCount.current = 0;
                            successDetectCount.current = 0;
                            facePositions.current = defaultFacePositions;
                            setStatus(VerificationStatusEnum.POOR_LIGHTING);

                            return;
                        }
                        failedChecksCount.current += 1;
                        facePositions.current = defaultFacePositions;
                        setStatus(VerificationStatusEnum.ALIGN_FACE);
                        detectCount.current = 0;
                        successDetectCount.current = 0;
                        isPaused.current = false;
                    }
                }
            }
        },
        [onFileChange, onSubmit],
    );

    const tryLoadFacemesh = useCallback(async (isSilent?: boolean) => {
        try {
            setStatus(
                isSilent
                    ? VerificationStatusEnum.FACE_DETECTOR_LOADING_SILENT
                    : VerificationStatusEnum.FACE_DETECTOR_LOADING,
            );

            const result = await Promise.race([
                faceDetection.createDetector(
                    faceDetection.SupportedModels.MediaPipeFaceDetector,
                    FACE_DETECTOR_CONFIG,
                ),
                sleep(FACE_DETECTOR_LOADING_TIMEOUT),
            ]);

            if (result) {
                faceDetector.current = result as FaceLandmarksDetector;
            } else {
                setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);

                return false;
            }

            return true;
        } catch (e) {
            setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);

            return false;
        }
    }, []);

    // Third step - make detections
    const detect = useCallback(async (): Promise<FaceDetectResult | undefined> => {
        if (
            typeof webcamRef.current !== 'undefined' &&
            webcamRef.current !== null &&
            webcamRef?.current?.video?.readyState === 4 &&
            faceDetector.current
        ) {
            const { video } = webcamRef.current;
            const { videoWidth } = webcamRef.current?.video;
            const { videoHeight } = webcamRef.current?.video;

            webcamRef.current.video.width = videoWidth || 720;
            webcamRef.current.video.height = videoHeight || 480;

            let faces: Face[] = [];

            try {
                faces = await faceDetector.current.estimateFaces(video);
            } catch (e) {
                await tryLoadFacemesh(true);
            }

            detectCount.current += 1;

            if (faces.length !== faceCount.current && faces.length > 1) {
                faceCount.current = faces.length;
                await capture({
                    documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO_EXTRA,
                    tag: DocsVerificationLivenessTagErrorEnum.MULTIPARTY,
                    isIgnoreCheck: true,
                    file: await getWebcamFile(),
                });
            }

            if (faces[0]) {
                return detectFace(
                    faces[0],
                    {
                        width: videoWidth,
                        height: videoHeight,
                    },
                    MIN_FACE_SIZE_PERCENT + failedChecksCount.current + successDetectCount.current,
                );
            }

            setStatus(VerificationStatusEnum.FACE_NOT_FOUND);
        }

        return undefined;
    }, [capture, tryLoadFacemesh, getWebcamFile]);

    const checkFaceDetectedResult = useCallback(
        async (res: FaceDetectResult) => {
            if (Object.values(res)) {
                if (res.isFaceFound) {
                    setStatus(VerificationStatusEnum.FACE_FOUND);
                    if (res.isSuccess) {
                        successDetectCount.current += 1;
                        const file = await getWebcamFile();

                        filesToUpload.current.push(file);
                        if (successDetectCount.current >= SUCCESS_DETECT_COUNT) {
                            isPaused.current = true;
                            await capture({
                                documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
                                detectResult: res,
                                isIgnoreCheck: false,
                                file: await getWebcamFile(),
                            });
                        }
                    }
                } else {
                    setStatus(VerificationStatusEnum.FACE_NOT_FOUND);
                }
            }

            if (!isPaused.current) {
                setFaceDetectResult(res);
            }
        },
        [capture, getWebcamFile],
    );

    useEffect(() => {
        navigator.mediaDevices
            .getUserMedia(cameraConstraints)
            .then((stream) => {
                const tracks = stream.getTracks();

                tracks?.forEach((track) => {
                    track.stop();
                });
                setCurrentStep(DocsVerificationInternalStepEnum.SCANNING);
            })
            .catch(() => {
                setIsCameraNotAllowed(true);
            });
    }, [cameraConstraints]);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;

            const prepare = async () => {
                setPrepareSeconds(PREPARE_SECONDS);
                let seconds = PREPARE_SECONDS;

                while (seconds > 0) {
                    webcamRef.current?.getScreenshot();
                    // eslint-disable-next-line no-await-in-loop
                    await sleep(1000);
                    seconds -= 1;
                    setPrepareSeconds(seconds);
                }
            };

            const loadFaceMash = async () => {
                if (!faceDetector.current) {
                    const isLoaded = await tryLoadFacemesh();

                    if (!isLoaded) {
                        return;
                    }
                }

                setStatus(VerificationStatusEnum.PREPARING);
                setTimeout(() => {
                    scrollTop();
                });
                // one detect to make load face detector and prevent lag
                await Promise.all([prepare(), detect()]);
                setStatus(VerificationStatusEnum.READY);
            };

            const startDetecting = async () => {
                if (isDone.current && faceDetectInterval.current) {
                    clearInterval(faceDetectInterval.current);

                    return;
                }

                if (isPaused.current || isDetectInProgress.current) {
                    return;
                }

                isDetectInProgress.current = true;
                const res = await detect();

                isDetectInProgress.current = false;

                if (res) {
                    await checkFaceDetectedResult(res);
                }
            };

            const initialize = async () => {
                await loadFaceMash();
                await sleep(1500);
                faceDetectInterval.current = setInterval(startDetecting, 1000);
            };

            initialize();
        }

        return () => {
            if (faceDetectInterval.current) {
                clearInterval(faceDetectInterval.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkFaceDetectedResult, detect, tryLoadFacemesh]);

    const handleMakePhotoClick = useCallback(async () => {
        const file = await getWebcamFile();

        filesToUpload.current = [file, file, file, file];
        await capture({
            documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
            detectResult: faceDetectResult as FaceDetectResult,
            isIgnoreCheck: false,
            file,
        });
    }, [capture, faceDetectResult, getWebcamFile]);

    const handleUploadFileClick = useCallback(async () => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files) {
                isPaused.current = true;
                setStatus(VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS);
                const file = event.target.files[0] as FileWithPreview;

                file.preview = URL.createObjectURL(file);
                filesToUpload.current = [file, file, file, file];
                await capture({
                    documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
                    detectResult: faceDetectResult as FaceDetectResult,
                    isIgnoreCheck: false,
                    file,
                });
            }
        },
        [capture, faceDetectResult],
    );

    useEffect(() => {
        if (uploadFileRef) {
            uploadFileRef.current = handleFileChange;
        }
    }, [handleFileChange, uploadFileRef]);

    return (
        <ProcessLivenessContainer ref={containerRef} className={className}>
            {isCameraNotAllowed ? (
                <CameraAccessDefault title={titleCameraNotAllowed} onClose={onClose} />
            ) : (
                <React.Fragment>
                    {currentStep === DocsVerificationInternalStepEnum.CAMERA_ACCESS && (
                        <div className='spinner-container'>
                            <Spinner className='spinner' />
                        </div>
                    )}
                    {currentStep === DocsVerificationInternalStepEnum.SCANNING && (
                        <React.Fragment>
                            <Hints
                                faceDetectResult={faceDetectResult}
                                verificationStatus={status}
                                prepareSeconds={prepareSeconds}
                                tryLoadFacemesh={tryLoadFacemesh}
                                detectCount={detectCount.current}
                                minDetectCount={
                                    failedChecksCount.current >= 1 ? 0 : MIN_DETECT_COUNT
                                }
                            />
                            <WebCamContainer
                                ref={webContainerRef}
                                className={cn({
                                    'mask-ready': isMaskReady,
                                })}
                            >
                                <Webcam
                                    ref={webcamRef}
                                    audio={false}
                                    mirrored={true}
                                    videoConstraints={cameraConstraints.video}
                                    screenshotFormat='image/jpeg'
                                    screenshotQuality={1}
                                    forceScreenshotSourceSize={true}
                                    minScreenshotHeight={2160}
                                    minScreenshotWidth={4096}
                                />
                                <Mask
                                    isLoading={
                                        status ===
                                            VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS ||
                                        status === VerificationStatusEnum.SUCCESS
                                    }
                                    isError={status === VerificationStatusEnum.FACE_NOT_FOUND}
                                    video={webcamRef?.current?.video}
                                    onReady={maskReady}
                                />
                            </WebCamContainer>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
            {!matchesMobileSm && !isCameraNotAllowed && (
                <Icon
                    icon={<CloseIcon />}
                    $clickable={true}
                    className='close-icon'
                    onClick={onClose}
                />
            )}
            <div className='liveness-process-actions'>
                {(getEnvVar('REACT_APP_IS_SDK_DEBUG') ||
                    userRole === OrganizationRoleEnum.ADMIN) && (
                    <div className='admin-actions'>
                        <Button
                            variant='primary'
                            className='take-photo-button'
                            onClick={handleMakePhotoClick}
                            size='xl'
                        >
                            {t('docVerificationWidget.camera.takePhoto')}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type='file'
                            name='image'
                            accept='image/*'
                            multiple={false}
                            onChange={handleFileChange}
                            className='hidden'
                        />
                        <Button
                            variant='primary'
                            onClick={handleUploadFileClick}
                            size='xl'
                            loading={status === VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS}
                        >
                            {t('common.uploadFile')}
                        </Button>
                    </div>
                )}
            </div>
        </ProcessLivenessContainer>
    );
};
